import React from 'react';

/**
 * ExplainPanel: shows the reasoning trail behind the most recent NLP query.
 * Reads from `explainData` populated by ChatInterface after each successful query.
 *
 * Renders as a slide-out drawer, dismissible.
 */
export default function ExplainPanel({ explainData, onClose }) {
  if (!explainData) return null;
  const {
    naturalQuery,
    intent,
    layer,
    conditions = [],
    sources = [],
    resultCount,
    isProximity,
    leakCount,
    premiseCount,
    withinFt,
    premiseType,
    groupBy,
    timestamp,
    fallbackUsed,
    classifier
  } = explainData;

  // Build a story-aware plain-English summary that mirrors the chat narrative.
  const premiseLabel = premiseType === 'school' ? 'school' : premiseType === 'hospital' ? 'hospital' : 'school or hospital';
  const intentNoun = layer === 'pipe_risk' ? 'pipe replacement priority'
    : layer === 'leaks' ? 'leak detection'
    : layer === 'crews' || layer === 'work_orders_open' ? 'crew dispatch'
    : layer === 'fenceline' || layer === 'sensors' || layer === 'dispersion_plumes' || layer === 'scada' ? 'fenceline incident response'
    : (intent || layer || 'spatial query');
  let storyLine;
  if (isProximity) {
    const premiseNoun = premiseType === 'school'
      ? (premiseCount === 1 ? 'school' : 'schools')
      : premiseType === 'hospital'
      ? (premiseCount === 1 ? 'hospital' : 'hospitals')
      : (premiseCount === 1 ? 'sensitive premise' : 'sensitive premises');
    storyLine = <>
      The AI interpreted your question as a <strong>safety / proximity check</strong>, queried{' '}
      <strong>{sources.join(', ') || 'GIS Server'}</strong>, computed distance between leaks and sensitive premises,
      and found <strong>{leakCount}</strong> leak{leakCount === 1 ? '' : 's'} within{' '}
      <strong>{withinFt || 100} ft</strong> of <strong>{premiseCount}</strong>{' '}
      {premiseNoun}. These qualify for accelerated repair under <strong>49 CFR §192.624</strong>.
    </>;
  } else if (layer === 'pipe_risk') {
    storyLine = <>
      The AI ran a <strong>capital-planning ranking</strong>{groupBy === 'district' ? <> grouped <strong>by district</strong></> : null},
      blending spatial pipe data with the last 3 quarterly survey PDFs. Returned <strong>{resultCount}</strong> scored segments
      from <strong>{sources.join(', ') || 'GIS Server'}</strong>.
    </>;
  } else if (layer === 'crews' || layer === 'work_orders_open') {
    storyLine = <>
      The AI looked up <strong>crew availability</strong> from <strong>{sources.join(', ') || 'SharePoint · Crew Dispatch Schedule'}</strong>,
      cross-referenced open work orders, and returned <strong>{resultCount}</strong> crew{resultCount === 1 ? '' : 's'}
      with assigned segments and ETAs.
    </>;
  } else if (layer === 'fenceline' || layer === 'sensors' || layer === 'dispersion_plumes' || layer === 'scada') {
    storyLine = <>
      The AI ran a <strong>fenceline incident response</strong> workflow: pulled triggered sensor readings, queried{' '}
      <strong>{sources.join(', ') || 'SCADA telemetry'}</strong>, applied wind-vector + Gaussian-plume back-projection,
      and returned <strong>{resultCount}</strong> attributed event{resultCount === 1 ? '' : 's'}.
      Tier 1 regulator notification clock starts at 4 hours from first detection.
    </>;
  } else if (layer === 'leaks') {
    storyLine = <>
      The AI interpreted your question as <strong>leak detection</strong>
      {conditions.length > 0 ? <>, applied <strong>{conditions.length}</strong> filter{conditions.length === 1 ? '' : 's'}</> : null},
      queried <strong>{sources.join(', ') || 'GIS Server'}</strong>, and returned{' '}
      <strong>{resultCount}</strong> leak{resultCount === 1 ? '' : 's'} ranked by risk score.
    </>;
  } else {
    storyLine = <>
      The AI interpreted your question as a <strong>{intentNoun}</strong>
      {conditions.length > 0 ? <>, applied <strong>{conditions.length}</strong> filter{conditions.length === 1 ? '' : 's'}</> : null}
      {sources.length > 0 ? <>, pulled data from <strong>{sources.join(', ')}</strong></> : null}
      , and returned <strong>{resultCount ?? 0}</strong> matching record{(resultCount ?? 0) === 1 ? '' : 's'}.
    </>;
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={drawer} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Trust & Explainability</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>How this result was reached</div>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={section}>
          <div style={label}>Question asked</div>
          <div style={{ ...value, fontStyle: 'italic', color: '#0f172a' }}>"{naturalQuery || '—'}"</div>
        </div>

        <div style={section}>
          <div style={label}>How the AI classified it</div>
          <div style={value}>
            <div>Intent: <strong>{intent || layer || 'unknown'}</strong></div>
            <div style={{ color: '#64748b', marginTop: 2 }}>Engine: {classifier || 'Claude (Anthropic)'} · {fallbackUsed ? 'regex fallback used' : 'LLM classification'}</div>
          </div>
        </div>

        {conditions.length > 0 && (
          <div style={section}>
            <div style={label}>Filters applied</div>
            <div style={value}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', color: '#64748b', fontWeight: 500, padding: '4px 8px 4px 0', fontSize: 11 }}>Field</th>
                    <th style={{ textAlign: 'left', color: '#64748b', fontWeight: 500, padding: '4px 8px', fontSize: 11 }}>Operator</th>
                    <th style={{ textAlign: 'left', color: '#64748b', fontWeight: 500, padding: '4px 8px', fontSize: 11 }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {conditions.map((c, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '6px 8px 6px 0', fontFamily: 'ui-monospace, monospace', color: '#0f172a' }}>{c.field}</td>
                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{c.operator}</td>
                      <td style={{ padding: '6px 8px', color: '#0f172a' }}>{Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={section}>
          <div style={label}>Data sources queried</div>
          <div style={value}>
            {sources.length === 0 ? (
              <span style={{ color: '#64748b' }}>—</span>
            ) : sources.map((s, i) => (
              <span key={i} style={chip}>{s}</span>
            ))}
          </div>
        </div>

        <div style={section}>
          <div style={label}>Result</div>
          <div style={value}>
            <strong>{resultCount ?? 0}</strong> records returned
            {layer && <span style={{ color: '#64748b' }}> from <span style={{ fontFamily: 'ui-monospace, monospace' }}>{layer}</span></span>}
          </div>
        </div>

        <div style={section}>
          <div style={label}>Plain-English summary</div>
          <div style={{ ...value, lineHeight: 1.6 }}>
            {storyLine}
            {fallbackUsed && <> Note: the LLM was unavailable so a regex fallback was used — accuracy may be lower.</>}
          </div>
        </div>

        <div style={{ ...section, color: '#64748b', fontSize: 11, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          Audited at {timestamp ? new Date(timestamp).toLocaleString() : new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 9999, display: 'flex', justifyContent: 'flex-end' };
const drawer = { width: 'min(540px, 100%)', height: '100%', background: 'white', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', overflowY: 'auto', padding: '24px 24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' };
const header = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 16, borderBottom: '1px solid #e2e8f0', marginBottom: 16 };
const closeBtn = { background: 'transparent', border: 'none', fontSize: 28, cursor: 'pointer', color: '#94a3b8', lineHeight: 1, padding: 0 };
const section = { marginBottom: 16 };
const label = { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 6 };
const value = { fontSize: 13, color: '#0f172a' };
const chip = { display: 'inline-block', padding: '2px 10px', margin: '2px 6px 2px 0', background: '#eff6ff', color: '#1d4ed8', borderRadius: 12, fontSize: 12, fontWeight: 500 };
